import { useCallback, useState, useSyncExternalStore } from "react";

// Com'è la rail su desktop (riscontro del product owner del 3 ottobre 2026: «le liste sotto le smart list… dovrebbero
// essere già a vista»; le soglie e il pulsante sono la questione aperta 183 di `DECISIONS.md`). Da 1280 px la rail è
// aperta e fissa: etichette e Smart List sempre in vista, il contenuto le sta accanto. Sotto parte a icone. «Riduci il
// menu» la chiude e «Allarga il menu» la apre (decisione del product owner del 4 ottobre 2026): a icone resta chiusa anche
// con il puntatore o il focus dentro, mai un pannello sopra la pagina. Il pulsante, in fondo alla rail, c'è da 1200 px, e
// la scelta resta su questo dispositivo (`localStorage`) sopra quella di partenza. Fra 721 e 1199 px la rail resta a
// icone, senza il pulsante: fissa, toglierebbe alla pagina lo spazio che le pagine danno per scontato sopra i 980 px della
// finestra (`test/layout-width.test.ts`); lì la rail non elenca le Smart List (questione aperta 271).
// Sul telefono (fino a 720 px) la rail resta il pannello di «Menu» e la scelta non conta.

/** Nel `localStorage` del dispositivo: `"open"` (aperta e fissa) o `"icons"`; senza, decide la larghezza. */
export const RAIL_PREFERENCE_KEY = "gioia365.rail";

export type RailPreference = "open" | "icons";

/** Da qui la rail parte aperta e fissa. */
export const RAIL_WIDE_QUERY = "(min-width: 1280px)";
/** Da qui la rail si può fissare con «Allarga il menu»: accanto a lei restano almeno 912 px, quanti a 980 px con le icone. */
export const RAIL_PIN_QUERY = "(min-width: 1200px)";
/** Da qui la rail è a sinistra del contenuto; sotto, il pannello di «Menu» (lo stesso limite di `app-shell.css`). */
export const RAIL_DESKTOP_QUERY = "(min-width: 721px)";

/** Il `localStorage` del dispositivo, oppure `null` se il browser lo nega. */
function deviceStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** La scelta ricordata su questo dispositivo; nessuna con un valore sconosciuto o un'archiviazione che non risponde. */
export function readRailPreference(storage: Storage | null): RailPreference | null {
  if (!storage) return null;
  try {
    const value = storage.getItem(RAIL_PREFERENCE_KEY);
    return value === "open" || value === "icons" ? value : null;
  } catch {
    return null;
  }
}

/** Ricorda la scelta su questo dispositivo; senza archiviazione vale finché la pagina resta aperta. */
export function writeRailPreference(storage: Storage | null, value: RailPreference): void {
  if (!storage) return;
  try {
    storage.setItem(RAIL_PREFERENCE_KEY, value);
  } catch {
    // Archiviazione negata dal browser: la scelta vale fino alla prossima apertura.
  }
}

/**
 * Se la rail è aperta e fissa: mai dove non la si può fissare (sotto i 1200 px, telefono compreso); altrimenti la scelta
 * ricordata, o aperta soltanto su schermo largo.
 */
export function railPinned(input: { canPin: boolean; wide: boolean; preference: RailPreference | null }): boolean {
  if (!input.canPin) return false;
  return input.preference === null ? input.wide : input.preference === "open";
}

/** Se la media query corrisponde adesso; segue i cambi della finestra. Senza `matchMedia`, no. */
function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (notify: () => void) => {
      if (typeof window.matchMedia !== "function") return () => {};
      const list = window.matchMedia(query);
      list.addEventListener("change", notify);
      return () => list.removeEventListener("change", notify);
    },
    [query],
  );
  return useSyncExternalStore(subscribe, () => typeof window.matchMedia === "function" && window.matchMedia(query).matches);
}

/**
 * La rail del guscio: se è aperta e fissa, se è accanto al contenuto (`desktop`, non il pannello del telefono), se la si
 * può fissare (il pulsante c'è) e il pulsante che la passa a icone o di nuovo aperta.
 */
export function useRailMode(): { pinned: boolean; desktop: boolean; canPin: boolean; toggle(): void } {
  const desktop = useMediaQuery(RAIL_DESKTOP_QUERY);
  const canPin = useMediaQuery(RAIL_PIN_QUERY);
  const wide = useMediaQuery(RAIL_WIDE_QUERY);
  const [preference, setPreference] = useState(() => readRailPreference(deviceStorage()));
  const pinned = railPinned({ canPin, wide, preference });
  const toggle = useCallback(() => {
    const next: RailPreference = pinned ? "icons" : "open";
    writeRailPreference(deviceStorage(), next);
    setPreference(next);
  }, [pinned]);
  return { pinned, desktop, canPin, toggle };
}
